// Phone number masking utilities

/**
 * Format phone number for display
 * @param {string} phone - Raw phone number
 * @returns {string} Formatted phone number
 */
export const formatPhoneNumber = (phone) => {
  if (!phone) return "";
  // Remove all non-digit characters
  const digits = phone.replace(/\D/g, "");
  
  // Format: +### ## ## # #####
  // Example: +225 27 22 4 82626
  if (digits.length === 0) return "";
  if (digits.length <= 3) return `+${digits}`;
  if (digits.length <= 5) return `+${digits.slice(0, 3)} ${digits.slice(3)}`;
  if (digits.length <= 7) return `+${digits.slice(0, 3)} ${digits.slice(3, 5)} ${digits.slice(5)}`;
  if (digits.length <= 8) return `+${digits.slice(0, 3)} ${digits.slice(3, 5)} ${digits.slice(5, 7)} ${digits.slice(7)}`;
  
  // Full format: +### ## ## # #####
  return `+${digits.slice(0, 3)} ${digits.slice(3, 5)} ${digits.slice(5, 7)} ${digits.slice(7, 8)} ${digits.slice(8, 13)}`;
};

/**
 * Clean phone number for storage (remove formatting)
 * @param {string} phone - Formatted phone number
 * @returns {string} Clean phone number
 */
export const cleanPhoneNumber = (phone) => {
  if (!phone) return "";
  // Remove all non-digit characters except +
  return phone.replace(/[^\d+]/g, "");
};

/**
 * Apply phone number mask to input element
 * @param {HTMLElement} input - Input element to apply mask to
 * @param {string} countryCode - Country code (e.g., 'CI', 'GH', etc.)
 */
export const applyPhoneMask = (input, countryCode = null) => {
  if (!input) return;
  
  // Country code mapping
  const countryCodes = {
    'BF': '+226', // Burkina Faso
    'BJ': '+229', // Benin
    'CI': '+225', // Côte d'Ivoire
    'CV': '+238', // Cape Verde
    'GH': '+233', // Ghana
    'GN': '+224', // Guinea
    'GW': '+245', // Guinea-Bissau
    'LR': '+231', // Liberia
    'ML': '+223', // Mali
    'MR': '+222', // Mauritania
    'NE': '+227', // Niger
    'NG': '+234', // Nigeria
    'SN': '+221', // Senegal
    'SL': '+232', // Sierra Leone
    'TG': '+228', // Togo
  };
  
  const currentCountryCode = countryCode ? countryCodes[countryCode] : '+225'; // Default to Ivory Coast
  let wasEmpty = !input.value || input.value.trim() === ''; // Track if field was empty
  
  const formatInput = (value) => {
    // Remove all non-digit characters
    let digits = value.replace(/\D/g, '');
    
    // If no digits, return empty string (field is editable)
    if (digits.length === 0) return '';
    
    // If digits start with country code digits (e.g., 225), remove them
    const countryDigits = currentCountryCode.replace(/\D/g, '');
    if (digits.startsWith(countryDigits)) {
      digits = digits.slice(countryDigits.length);
    }
    
    // Limit to maximum 10 digits for local number
    if (digits.length > 10) {
      digits = digits.slice(0, 10);
    }
    
    // If no remaining digits after removing country code, just return country code
    if (digits.length === 0) return currentCountryCode;
    
    // Format the local number part: ## ## ## ## ##
    // Example: +225 00 00 00 00 00
    if (digits.length <= 2) return `${currentCountryCode} ${digits}`;
    if (digits.length <= 4) return `${currentCountryCode} ${digits.slice(0, 2)} ${digits.slice(2)}`;
    if (digits.length <= 6) return `${currentCountryCode} ${digits.slice(0, 2)} ${digits.slice(2, 4)} ${digits.slice(4)}`;
    if (digits.length <= 8) return `${currentCountryCode} ${digits.slice(0, 2)} ${digits.slice(2, 4)} ${digits.slice(4, 6)} ${digits.slice(6)}`;
    if (digits.length <= 10) return `${currentCountryCode} ${digits.slice(0, 2)} ${digits.slice(2, 4)} ${digits.slice(4, 6)} ${digits.slice(6, 8)} ${digits.slice(8)}`;
    
    // Full format with max 10 digits for local number: +### ## ## ## ## ##
    return `${currentCountryCode} ${digits.slice(0, 2)} ${digits.slice(2, 4)} ${digits.slice(4, 6)} ${digits.slice(6, 8)} ${digits.slice(8, 10)}`;
  };
  
  const handleInput = (e) => {
    const input = e.target;
    const value = input.value;
    
    // Update wasEmpty flag
    if (value.length === 0) {
      wasEmpty = true;
      return;
    } else {
      wasEmpty = false;
    }
    
    // Count only digits to determine if we should allow more input
    const allDigits = value.replace(/\D/g, '');
    const countryDigits = currentCountryCode.replace(/\D/g, '');
    let localDigits = allDigits;
    
    // Remove country code digits if present
    if (allDigits.startsWith(countryDigits)) {
      localDigits = allDigits.slice(countryDigits.length);
    }
    
    // Maximum 10 digits for local number
    if (localDigits.length > 10) {
      // Prevent further input by restoring previous value
      e.preventDefault();
      return;
    }
    
    // If value already starts with the current country code, format it properly
    if (value.startsWith(currentCountryCode)) {
      const formatted = formatInput(value);
      if (formatted !== value) {
        input.value = formatted;
        input.setSelectionRange(formatted.length, formatted.length);
      }
      return;
    }
    
    // Check if field was empty and user types a digit
    if (wasEmpty && value.length === 1 && /^\d$/.test(value)) {
      input.value = `${currentCountryCode} ${value}`;
      input.setSelectionRange(currentCountryCode.length + 1, currentCountryCode.length + 1);
      wasEmpty = false;
      return;
    }
    
    // Only format if there are digits and no country code yet
    if (value && !value.startsWith('+')) {
      const digits = value.replace(/\D/g, '');
      if (digits.length > 0) {
        const formatted = formatInput(value);
        input.value = formatted;
        input.setSelectionRange(formatted.length, formatted.length);
        return;
      }
    }
  };
  
  const handleKeydown = (e) => {
    const input = e.target;
    const value = input.value;
    
    // Allow: backspace, delete, tab, escape, enter, and navigation keys
    if ([8, 9, 27, 13, 46, 37, 38, 39, 40].indexOf(e.keyCode) !== -1) {
      return;
    }
    
    // Check if we're trying to type a digit
    const isDigit = (e.keyCode >= 48 && e.keyCode <= 57) || (e.keyCode >= 96 && e.keyCode <= 105);
    
    if (isDigit) {
      // Count current digits to see if we can add more
      const allDigits = value.replace(/\D/g, '');
      const countryDigits = currentCountryCode.replace(/\D/g, '');
      let localDigits = allDigits;
      
      // Remove country code digits if present
      if (allDigits.startsWith(countryDigits)) {
        localDigits = allDigits.slice(countryDigits.length);
      }
      
      // If we already have 10 local digits, prevent more digit input
      if (localDigits.length >= 10) {
        e.preventDefault();
        return;
      }
      
      return; // Allow the digit
    }
    
    // Allow plus sign (+) only at the beginning
    if ((e.keyCode === 187 || e.keyCode === 107) && value.length === 0) {
      return;
    }
    
    // Prevent all other keys
    e.preventDefault();
  };
  
  const handleFocus = (e) => {
    const input = e.target;
    const value = input.value;
    
    // Update wasEmpty flag based on current value
    wasEmpty = !value || value.trim() === '';
  };

  // Remove any existing maxlength attribute since we handle limits dynamically
  input.removeAttribute('maxlength');

  // Add event listeners
  input.addEventListener('input', handleInput);
  input.addEventListener('keydown', handleKeydown);
  input.addEventListener('focus', handleFocus);
  
  // Initial formatting - format the existing value properly
  if (input.value) {
    const formatted = formatInput(input.value);
    if (formatted !== input.value) {
      input.value = formatted;
    }
  }
  
  // Return cleanup function
  return () => {
    input.removeEventListener('input', handleInput);
    input.removeEventListener('keydown', handleKeydown);
    input.removeEventListener('focus', handleFocus);
  };
};

/**
 * Vue directive for phone number masking
 */
export const phoneMaskDirective = {
  mounted(el, binding) {
    const input = el.querySelector('input') || el;
    if (input) {
      const countryCode = binding.value || null;
      el._phoneMaskCleanup = applyPhoneMask(input, countryCode);
    }
  },
  
  updated(el, binding) {
    const input = el.querySelector('input') || el;
    if (input) {
      // Clean up previous mask
      if (el._phoneMaskCleanup) {
        el._phoneMaskCleanup();
      }
      // Apply new mask with updated country code
      const countryCode = binding.value || null;
      el._phoneMaskCleanup = applyPhoneMask(input, countryCode);
    }
  },
  
  unmounted(el) {
    if (el._phoneMaskCleanup) {
      el._phoneMaskCleanup();
    }
  }
}; 